import React from 'react';

interface WeChatLoginModuleProps {
  qrLoading: boolean;
  qrExpired: boolean;
  qrTicket: Api.Account.WeChatOfficialQrTicketResponse | null;
  qrExchanging: boolean;
  qrStatusText: string;
  accountType?: 'PERSONAL' | 'ENTERPRISE';
  setAccountType?: (type: 'PERSONAL' | 'ENTERPRISE') => void;
  onAccountTypeChange?: (type: 'PERSONAL' | 'ENTERPRISE') => void;
  onRefreshQr: () => void;
}

export const WeChatLoginModule: React.FC<WeChatLoginModuleProps> = ({
  qrLoading,
  qrExpired,
  qrTicket,
  qrExchanging,
  qrStatusText,
  accountType = 'PERSONAL',
  setAccountType,
  onAccountTypeChange,
  onRefreshQr,
}) => {
  const isEnterprise = accountType === 'ENTERPRISE';
  const isPersonal = accountType === 'PERSONAL';

  const handleSelectType = (type: 'PERSONAL' | 'ENTERPRISE') => {
    if (accountType !== type) {
      setAccountType?.(type);
      onAccountTypeChange?.(type);
    }
  };

  return (
    <div className="flex flex-col items-center py-2 animate-[fadeIn_0.2s_ease-out]">
      {/* 账号类型切换 */}
      {setAccountType && (
        <div className="w-full flex items-center bg-gray-100 rounded-full p-1 mb-5">
          <button
            type="button"
            onClick={() => handleSelectType('PERSONAL')}
            className={`flex-1 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
              isPersonal
                ? 'bg-white text-foreground-950 shadow-sm font-semibold'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-user-3-line text-sm"></i>
            个人账号
          </button>
          <button
            type="button"
            onClick={() => handleSelectType('ENTERPRISE')}
            className={`flex-1 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
              isEnterprise
                ? 'bg-white text-foreground-950 shadow-sm font-semibold'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className="ri-building-2-line text-sm"></i>
            企业账号
          </button>
        </div>
      )}

      {/* 二维码容器 */}
      <div className="relative w-52 h-52 p-3 bg-white rounded-2xl border border-gray-200/80 shadow-inner flex items-center justify-center group">
        {qrLoading ? (
          <div className="flex flex-col items-center gap-2 text-foreground-400">
            <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
            <span className="text-xs">加载二维码中...</span>
          </div>
        ) : qrExpired ? (
          <div className="absolute inset-0 bg-white/95 rounded-2xl flex flex-col items-center justify-center p-4 text-center z-10 animate-[fadeIn_0.2s_ease-out]">
            <i className="ri-qr-code-line text-4xl text-gray-400 mb-1"></i>
            <span className="text-xs text-foreground-600 mb-3">二维码已失效</span>
            <button
              type="button"
              onClick={onRefreshQr}
              className="px-4 py-1.5 rounded-lg bg-primary-500 text-foreground-950 text-xs font-semibold hover:bg-primary-400 transition-all cursor-pointer shadow-sm"
            >
              刷新二维码
            </button>
          </div>
        ) : qrTicket?.qr_code_url ? (
          <>
            <img
              src={qrTicket.qr_code_url}
              alt="微信公众号登录二维码"
              className="w-full h-full object-contain rounded-xl"
            />
            {qrExchanging && (
              <div className="absolute inset-0 bg-white/90 rounded-2xl flex flex-col items-center justify-center gap-2 z-10">
                <i className="ri-checkbox-circle-fill text-4xl text-emerald-500 animate-bounce"></i>
                <span className="text-xs text-emerald-700 font-medium">登录成功，请稍候...</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-foreground-400">
            <i className="ri-qr-scan-2-line text-3xl"></i>
            <span className="text-xs">未获取到二维码</span>
          </div>
        )}
      </div>

      {/* 状态指示条 */}
      <div className="mt-4 flex items-center gap-2 text-xs text-foreground-600 bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
        <span>{qrStatusText}</span>
      </div>

      {/* 底部功能按钮 */}
      <div className="mt-5 flex items-center gap-4">
        <button
          type="button"
          onClick={onRefreshQr}
          disabled={qrLoading}
          className="text-xs text-foreground-400 hover:text-primary-600 flex items-center gap-1 transition-colors cursor-pointer disabled:opacity-40"
        >
          <i className="ri-refresh-line"></i>
          手动刷新
        </button>
      </div>
    </div>
  );
};
